import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { fmtDate, formatApiError } from "@/lib/api";
import { PageHeader, StatCard, Avatar, useTableSort, SortableTH } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { GraduationCap, Plus, Trash2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { EmployeePicker, useEmployees, today, inDays } from "@/pages/people/parts";
import { Attachments, FileStager, uploadFiles } from "@/components/Attachments";

export default function Training() {
  const [data, setData] = useState({ records: [], can_manage: false });
  const [expiryF, setExpiryF] = useState("all");
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const employees = useEmployees();
  const [f, setF] = useState({ employee_id: "", course: "", provider: "", completed_date: "", expiry_date: "", status: "completed", notes: "" });
  const [files, setFiles] = useState([]);

  const load = useCallback(() => api.get("/people/training").then((r) => setData(r.data)).catch(() => setData({ records: [], can_manage: false })), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!f.employee_id) { toast.error("Choose an employee"); return; }
    if (!f.course.trim()) { toast.error("Enter a course name"); return; }
    const { employee_id, ...body } = f;
    try { const { data: rec } = await api.post(`/employees/${employee_id}/training`, body); if (files.length) await uploadFiles(`/training/${rec.id}`, files); toast.success("Training added"); setShowAdd(false); setF({ employee_id: "", course: "", provider: "", completed_date: "", expiry_date: "", status: "completed", notes: "" }); setFiles([]); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/training/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const soon = (d) => d && d >= today() && d <= inDays(60);
  const expired = (d) => d && d < today();

  const rows = useMemo(() => data.records.filter((r) => {
    if (expiryF === "expired" && !expired(r.expiry_date)) return false;
    if (expiryF === "soon" && !soon(r.expiry_date)) return false;
    if (q && !`${r.employee_name} ${r.course || ""} ${r.provider || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [data.records, expiryF, q]);

  const { sorted, sort, toggle } = useTableSort(rows, {
    employee: (r) => r.employee_name, course: (r) => r.course, provider: (r) => r.provider,
    completed: (r) => r.completed_date, expiry: (r) => r.expiry_date,
  }, { key: "expiry", dir: "desc" });

  const expiredCount = data.records.filter((r) => expired(r.expiry_date)).length;
  const soonCount = data.records.filter((r) => soon(r.expiry_date)).length;

  return (
    <div className="space-y-6" data-testid="people-training-page">
      <PageHeader title="Training" subtitle="Courses, certifications and renewal dates for the whole team.">
        {data.can_manage && <Button className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" onClick={() => setShowAdd(true)} data-testid="train-add-btn"><Plus size={15} className="mr-1" />Add training</Button>}
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard testid="train-stat-total" label="Records" value={data.records.length} icon={GraduationCap} accent="blue" />
        <StatCard testid="train-stat-soon" label="Renewals due (60d)" value={soonCount} tone="amber" icon={CheckCircle2} accent="teal" />
        <StatCard testid="train-stat-expired" label="Expired" value={expiredCount} tone={expiredCount ? "red" : "navy"} icon={AlertTriangle} accent="amber" />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, course, provider…" className="w-64" data-testid="train-search" />
        <Select value={expiryF} onValueChange={setExpiryF}><SelectTrigger className="w-48" data-testid="train-expiry-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All records</SelectItem><SelectItem value="soon">Renewal due (60 days)</SelectItem><SelectItem value="expired">Expired</SelectItem></SelectContent></Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <SortableTH label="Employee" sortId="employee" sort={sort} onSort={toggle} />
            <SortableTH label="Course" sortId="course" sort={sort} onSort={toggle} />
            <SortableTH label="Provider" sortId="provider" sort={sort} onSort={toggle} />
            <SortableTH label="Completed" sortId="completed" sort={sort} onSort={toggle} />
            <SortableTH label="Expires" sortId="expiry" sort={sort} onSort={toggle} />
            {data.can_manage && <SortableTH label="" sortId="x" sort={sort} onSort={() => {}} />}
          </TableRow></TableHeader>
          <TableBody>
            {sorted.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">No training records found.</TableCell></TableRow> :
              sorted.map((r) => (
                <TableRow key={r.id} data-testid={`train-row-${r.id}`}>
                  <TableCell><Link to={`/employees/${r.employee_id}?tab=training`} className="flex items-center gap-2 hover:text-[#2EA3FD]"><Avatar name={r.employee_name} photo={r.employee_photo} size={26} /><span className="font-medium text-[#0B1A2F]">{r.employee_name}</span></Link></TableCell>
                  <TableCell className="text-sm font-medium text-[#0B1A2F]">{r.course}
                    <div className="mt-1"><Attachments basePath={`/training/${r.id}`} items={r.attachments || []} canManage={data.can_manage} /></div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{r.provider || "—"}</TableCell>
                  <TableCell className="text-sm">{r.completed_date ? fmtDate(r.completed_date) : "—"}</TableCell>
                  <TableCell className="text-sm">
                    {r.expiry_date ? fmtDate(r.expiry_date) : "—"}
                    {expired(r.expiry_date) && <span className="ml-2 text-[10px] bg-rose-100 text-rose-700 rounded-full px-2 py-0.5">Expired</span>}
                    {soon(r.expiry_date) && <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">Renewal due</span>}
                  </TableCell>
                  {data.can_manage && <TableCell className="text-right"><Button variant="ghost" size="sm" className="text-rose-600" onClick={() => del(r.id)} data-testid={`train-del-${r.id}`}><Trash2 size={14} /></Button></TableCell>}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="train-add-dialog">
          <DialogHeader><DialogTitle>Add training record</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs text-slate-500">Employee</Label><EmployeePicker value={f.employee_id} onChange={(v) => setF({ ...f, employee_id: v })} employees={employees} testid="train-employee-picker" /></div>
            <div><Label className="text-xs text-slate-500">Course</Label><Input value={f.course} onChange={(e) => setF({ ...f, course: e.target.value })} placeholder="e.g. First Aid at Work" data-testid="train-course-input" /></div>
            <div><Label className="text-xs text-slate-500">Provider</Label><Input value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })} /></div>
            <div><Label className="text-xs text-slate-500">Completed</Label><Input type="date" value={f.completed_date} onChange={(e) => setF({ ...f, completed_date: e.target.value })} /></div>
            <div><Label className="text-xs text-slate-500">Expires / renewal</Label><Input type="date" value={f.expiry_date} onChange={(e) => setF({ ...f, expiry_date: e.target.value })} data-testid="train-expiry-input" /></div>
            <div className="col-span-2"><Label className="text-xs text-slate-500">Documents (optional)</Label><FileStager files={files} setFiles={setFiles} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button><Button className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" onClick={add} data-testid="train-save-btn">Save training</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
